import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageCircle, ExternalLink, Copy, Package, Search } from "lucide-react";

/**
 * ShareCatalogDialog
 *  - Composes a curated WhatsApp catalog share
 *  - Creates it via POST /catalog-shares
 *  - Opens wa.me deep link with the message pre-filled
 * Props:
 *   open, setOpen (bool controllers)
 *   products (array of {id,title,design_code,weight_grams,net_weight,metal,purity})
 *   defaultContactId (optional)
 *   onCreated (optional callback with share)
 */
export function ShareCatalogDialog({ open, setOpen, products = [], defaultContactId = null, onCreated }) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [contactId, setContactId] = useState(defaultContactId || "");
  const [manualPhone, setManualPhone] = useState("");
  const [showPrices, setShowPrices] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [createdShare, setCreatedShare] = useState(null);

  useEffect(() => { if (!open) { setCreatedShare(null); setTitle(""); setNote(""); setManualPhone(""); setContactId(defaultContactId || ""); } }, [open, defaultContactId]);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/contacts", { params: { page: 1, page_size: 200 } }); setContacts(data.items || []); } catch {}
  })(); }, []);

  const filteredContacts = useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    if (!q) return contacts.slice(0, 100);
    return contacts.filter(c => (c.name || "").toLowerCase().includes(q) || (c.mobile || "").includes(q) || (c.whatsapp_number || "").includes(q)).slice(0, 100);
  }, [contacts, contactSearch]);

  const selectedContact = contacts.find(c => c.id === contactId);
  const derivedPhone = selectedContact?.whatsapp_number || selectedContact?.mobile || manualPhone;
  const cleanPhone = (derivedPhone || "").replace(/[^\d]/g, "");

  const messagePreview = useMemo(() => {
    const t = title.trim() || "TARU · Curated selection for you";
    const lines = [`*${t}*`];
    if (note.trim()) lines.push("", note.trim());
    lines.push("", `Handpicked pieces (${products.length}):`);
    products.slice(0, 6).forEach(p => {
      const wt = p.weight_grams ?? p.net_weight;
      const wtStr = wt ? ` — ${wt} g` : "";
      lines.push(`• ${p.design_code || p.sku || "—"} · ${p.title}${wtStr}`);
    });
    if (products.length > 6) lines.push(`  …and ${products.length - 6} more inside`);
    lines.push("", "View full catalog:", "{CATALOG_LINK}");
    lines.push("", "Reply here to reserve or ask about any piece.");
    return lines.join("\n");
  }, [title, note, products]);

  const composeWaLink = (share) => {
    const link = `${window.location.origin}/c/${share.share_token}`;
    const msg = messagePreview.replace("{CATALOG_LINK}", link);
    const encoded = encodeURIComponent(msg);
    const phone = cleanPhone;
    return phone ? `https://wa.me/${phone}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
  };

  const submit = async () => {
    if (!products.length) return toast.error("Pick at least one product");
    setBusy(true);
    try {
      const { data } = await api.post("/catalog-shares", {
        title: title.trim() || "TARU · Curated selection for you",
        note: note.trim() || null,
        product_ids: products.map(p => p.id),
        contact_id: contactId || null,
        whatsapp_number: manualPhone || null,
        show_prices: showPrices,
      });
      setCreatedShare(data);
      toast.success("Share ready — opening WhatsApp");
      const waUrl = composeWaLink(data);
      window.open(waUrl, "_blank", "noopener,noreferrer");
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!createdShare) return;
    const link = `${window.location.origin}/c/${createdShare.share_token}`;
    navigator.clipboard.writeText(link);
    toast.success("Catalog link copied");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="share-catalog-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <MessageCircle size={20} className="text-[#25D366]"/> Share on WhatsApp
          </DialogTitle>
        </DialogHeader>

        {!createdShare ? (
          <div className="space-y-4">
            {/* Products preview */}
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#4A5A50]">Selection ({products.length})</Label>
              <div className="mt-2 flex gap-2 overflow-x-auto pb-2">
                {products.map(p => (
                  <div key={p.id} className="shrink-0 w-24 border border-[#E4DFCF] rounded-lg overflow-hidden" data-testid={`share-preview-${p.design_code || p.id}`}>
                    <div className="aspect-square bg-[#F1EDDF] flex items-center justify-center">
                      {p.media?.[0] ? <img src={fileUrl(p.media[0].thumb_url || p.media[0].url)} alt="" className="w-full h-full object-cover"/> : <Package size={20} className="text-[#C9A24B]"/>}
                    </div>
                    <div className="px-1.5 py-1">
                      <div className="text-[9px] text-[#0F5E39] font-semibold truncate">{p.design_code || p.sku}</div>
                      <div className="text-[10px] font-medium truncate">{p.weight_grams ?? p.net_weight ?? "—"} g</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div><Label>Title (message headline)</Label>
              <Input value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="Diwali festive picks for you" className="mt-1" data-testid="share-title"/>
            </div>

            <div><Label>Note (optional)</Label>
              <Textarea rows={2} value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Handpicked from our new arrivals — let me know which catches your eye ✨" className="mt-1" data-testid="share-note"/>
            </div>

            {/* Contact picker */}
            <div>
              <Label>Send to (optional)</Label>
              <div className="mt-1 space-y-2">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5A50]"/>
                  <Input placeholder="Search contact by name or number…" className="pl-9" value={contactSearch} onChange={(e)=>setContactSearch(e.target.value)} data-testid="share-contact-search"/>
                </div>
                {contactSearch && (
                  <div className="max-h-40 overflow-y-auto border border-[#E4DFCF] rounded-lg divide-y divide-[#E4DFCF]" data-testid="share-contact-list">
                    {filteredContacts.slice(0, 20).map(c => (
                      <button key={c.id} onClick={() => { setContactId(c.id); setContactSearch(""); }} className="w-full text-left px-3 py-2 hover:bg-[#F1EDDF] text-sm" data-testid={`share-contact-opt-${c.id}`}>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-[#4A5A50]">{c.whatsapp_number || c.mobile || "no number"}</div>
                      </button>
                    ))}
                    {filteredContacts.length === 0 && <div className="px-3 py-4 text-xs text-[#4A5A50]">No matches</div>}
                  </div>
                )}
                {selectedContact && (
                  <div className="flex items-center justify-between bg-[#F6EED8] rounded-lg px-3 py-2 text-sm">
                    <div>
                      <div className="font-medium">{selectedContact.name}</div>
                      <div className="text-xs text-[#6E5416]">{selectedContact.whatsapp_number || selectedContact.mobile}</div>
                    </div>
                    <button onClick={() => setContactId("")} className="text-xs text-[#6E5416] hover:underline" data-testid="share-clear-contact">Clear</button>
                  </div>
                )}
                {!selectedContact && (
                  <Input placeholder="…or type a WhatsApp number directly (e.g. +91 98765 43210)" value={manualPhone} onChange={(e)=>setManualPhone(e.target.value)} data-testid="share-manual-phone"/>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between bg-[#F1EDDF] rounded-lg px-3 py-2">
              <div>
                <div className="text-sm font-medium">Show prices on the shared page</div>
                <div className="text-xs text-[#4A5A50]">Off if you want to quote separately.</div>
              </div>
              <Switch checked={showPrices} onCheckedChange={setShowPrices} data-testid="share-show-prices"/>
            </div>

            {/* Preview */}
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#4A5A50]">WhatsApp message preview</Label>
              <pre className="mt-1 whitespace-pre-wrap text-xs bg-[#F1EDDF] rounded-lg p-3 max-h-40 overflow-y-auto font-mono text-[#0F1E15]" data-testid="share-preview-text">{messagePreview.replace("{CATALOG_LINK}", "https://…/c/{token}")}</pre>
            </div>
          </div>
        ) : (
          // Post-create success view
          <div className="space-y-4">
            <div className="p-4 bg-[#E1F0E6] rounded-lg text-[#0F5E39]" data-testid="share-created-block">
              <div className="font-serif text-xl">Catalog ready to send</div>
              <div className="text-sm mt-1">If WhatsApp didn't open automatically, use the buttons below.</div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#4A5A50]">Shareable link</Label>
              <div className="flex items-center gap-2">
                <Input readOnly value={`${window.location.origin}/c/${createdShare.share_token}`} className="font-mono text-xs" data-testid="share-link-input"/>
                <Button variant="outline" size="icon" onClick={copyLink} title="Copy" data-testid="share-copy-link"><Copy size={14}/></Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => window.open(composeWaLink(createdShare), "_blank", "noopener,noreferrer")}
                className="rounded-full bg-[#25D366] hover:bg-[#1FAE55] text-white flex-1"
                data-testid="share-open-whatsapp"
              >
                <MessageCircle size={14} className="mr-2"/> Open in WhatsApp
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(`/c/${createdShare.share_token}`, "_blank")}
                className="rounded-full"
                data-testid="share-preview-page"
              >
                <ExternalLink size={14} className="mr-1"/> Preview
              </Button>
            </div>
          </div>
        )}

        {!createdShare && (
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !products.length} className="rounded-full bg-[#25D366] hover:bg-[#1FAE55] text-white" data-testid="share-submit">
              <MessageCircle size={14} className="mr-2"/> {busy ? "Preparing…" : `Share ${products.length} item${products.length === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
